import { useMemo } from "react";
import { useRegionNodes } from "../../hooks/useRegions.js";
import { jurisdictionLabel } from "./components/JurisdictionChips.js";

/** Resolves one taxonomy country id to the label its chip shows. */
export type CountryLabelResolver = (countryId: number) => string;

export type UseCountryNamesResult = {
  /** Hand straight to `createAwardColumns`; stable across renders. */
  getCountryLabel: CountryLabelResolver;
  loading: boolean;
  error: Error | null;
};

/**
 * Country names for the Awards list, resolved client-side.
 *
 * The list endpoint returns raw `jurisdictionCountryIds` and nothing else
 * (AWARDS_STAFF_UI_PLAN.md §3.5). There is no by-id or by-ids endpoint for
 * countries, so whichever layer resolved them would have to pull the whole tree
 * and build a Map — which is why the decision went to the layer that already
 * does it in five other places. `useRegionNodes()` caches the tree module-side
 * for the lifetime of the SPA and de-dupes in-flight requests, so the table
 * costs one fetch no matter how many rows it draws.
 *
 * Call this ONCE, from the page, and thread the result into the columns as
 * `getLabel`. Calling it inside the chip component would instantiate a hook per
 * row.
 *
 * Degradation is deliberate: while the tree is in flight — and permanently if
 * taxonomy is down — every chip shows the `Country ${id}` fallback. The awards
 * list neither waits for the tree nor fails with it.
 */
export function useCountryNames(): UseCountryNamesResult {
  const { nodes, loading, error } = useRegionNodes();

  const namesById = useMemo(() => {
    const map = new Map<number, string>();

    for (const node of nodes) {
      // The `kind === "country"` filter is a hard requirement, not a nicety: the
      // tree is flat and mixes regions with countries, and the two id spaces are
      // unrelated, so a region id CAN equal a country id. That collision is why
      // the rest of staff-ui keys these nodes by `${kind}:${id}` (see
      // `regionNodeKey` in hooks/useRegions.ts) and why research-api's taxonomy
      // client filters the same way. Without the filter an award would silently
      // pick up a region name like "Middle East and North Africa", and nothing
      // about the row would look wrong.
      if (node.kind !== "country") {
        continue;
      }

      // `api_region_country_v2` name columns are nullable — a null has already
      // white-screened a page once through `.localeCompare` (see
      // hooks/useRegions.test.ts) — so it never escapes this map.
      map.set(node.id, node.name ?? "—");
    }

    return map;
  }, [nodes]);

  // Memoised on the map rather than rebuilt per render: this function is a
  // dependency of the column definitions, and a fresh identity on every render
  // would rebuild all eight columns and re-render the table with them.
  const getCountryLabel = useMemo<CountryLabelResolver>(
    () => countryId => namesById.get(countryId) ?? jurisdictionLabel(countryId),
    [namesById],
  );

  return { getCountryLabel, loading, error };
}
